import axios, { AxiosRequestConfig, CancelToken } from 'axios'
import moment from 'moment'
import { axiosInstance } from '../../../core/axios'
import { ReportFactoryError } from './reportFactoryTypes'
import type {
  ReportFactoryAcceptedJob,
  ReportFactoryConfig,
  ReportFactoryJob,
  ReportFactoryRequest,
} from './reportFactoryTypes'

export { ReportFactoryError } from './reportFactoryTypes'

type RequestOptions = { cancelToken?: CancelToken }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const isPortfolioId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0

const isBenchmarkPercent = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= 100 &&
  Math.round(value * 10000) / 10000 === value

const isPortfolio = (
  value: unknown
): value is ReportFactoryConfig['portfolios'][number] =>
  isRecord(value) &&
  isNonEmptyString(value.sheetName) &&
  isPortfolioId(value.portfolioId) &&
  isBenchmarkPercent(value.benchmarkPercent)

const isRequestPortfolio = (
  value: unknown
): value is ReportFactoryRequest['portfolios'][number] =>
  isRecord(value) &&
  isPortfolioId(value.portfolioId) &&
  isBenchmarkPercent(value.benchmarkPercent)

const isReportFactoryConfig = (
  value: unknown
): value is ReportFactoryConfig => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.configVersion) ||
    !Array.isArray(value.portfolios) ||
    value.portfolios.length !== 5 ||
    !value.portfolios.every(isPortfolio) ||
    !isRecord(value.defaults)
  ) {
    return false
  }
  const { defaults, portfolios } = value
  return (
    new Set(portfolios.map(({ portfolioId }) => portfolioId)).size === 5 &&
    (defaults.groupBy === 'currency' || defaults.groupBy === 'class') &&
    typeof defaults.isAppliedAccountMTD === 'boolean' &&
    typeof defaults.useFullFundName === 'boolean'
  )
}

const isReportFactoryRequest = (
  value: unknown
): value is ReportFactoryRequest => {
  if (
    !isRecord(value) ||
    value.reportType !== 'mtd_performance_hk_excel' ||
    !isNonEmptyString(value.basedate) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(value.basedate) ||
    !moment(value.basedate, 'YYYY-MM-DD', true).isValid() ||
    !isNonEmptyString(value.configVersion) ||
    !Array.isArray(value.portfolios) ||
    value.portfolios.length < 1 ||
    value.portfolios.length > 5 ||
    !value.portfolios.every(isRequestPortfolio)
  ) {
    return false
  }
  return (
    new Set(value.portfolios.map(({ portfolioId }) => portfolioId)).size ===
      value.portfolios.length &&
    (value.groupBy === 'currency' || value.groupBy === 'class') &&
    typeof value.isAppliedAccountMTD === 'boolean' &&
    typeof value.useFullFundName === 'boolean'
  )
}

const getApiOrigin = (): string => {
  const configuredUrl = process.env.REACT_APP_REPORT_FACTORY_API_URL
  if (!configuredUrl) throw new ReportFactoryError('API_URL_NOT_CONFIGURED')
  try {
    const url = new URL(configuredUrl)
    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      url.pathname !== '/' ||
      url.search ||
      url.hash
    ) {
      throw new Error('Invalid API origin')
    }
    return url.origin
  } catch {
    throw new ReportFactoryError('API_URL_NOT_CONFIGURED')
  }
}

const getAuthorization = (): string => {
  const token = localStorage.getItem('token')
  if (!isNonEmptyString(token)) {
    throw new ReportFactoryError('TOKEN_NOT_AVAILABLE')
  }
  return `Bearer ${token}`
}

const getJobPath = (jobId: string): string =>
  `/exports/mtd-hk/jobs/${encodeURIComponent(jobId)}`

const isAcceptedJob = (
  value: unknown,
  apiOrigin: string
): value is ReportFactoryAcceptedJob => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.jobId) ||
    value.status !== 'QUEUED' ||
    !isNonEmptyString(value.statusUrl)
  ) {
    return false
  }
  try {
    const url = new URL(value.statusUrl, apiOrigin)
    return (
      url.origin === apiOrigin &&
      url.pathname === getJobPath(value.jobId) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash
    )
  } catch {
    return false
  }
}

const isHttpsUrl = (value: unknown): value is string => {
  if (!isNonEmptyString(value)) return false
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !url.username && !url.password
  } catch {
    return false
  }
}

const isJob = (value: unknown, jobId: string): value is ReportFactoryJob => {
  if (!isRecord(value) || value.jobId !== jobId) return false
  switch (value.status) {
    case 'QUEUED':
    case 'RUNNING':
      return true
    case 'FAILED':
      return isNonEmptyString(value.failureCode)
    case 'SUCCEEDED':
      return (
        isHttpsUrl(value.downloadUrl) &&
        isNonEmptyString(value.filename) &&
        Array.isArray(value.noDataPortfolios) &&
        isNonEmptyString(value.urlExpiresAt) &&
        Number.isFinite(Date.parse(value.urlExpiresAt))
      )
    default:
      return false
  }
}

const normalizeError = (error: unknown): never => {
  if (axios.isCancel(error) || error instanceof ReportFactoryError) throw error
  if (axios.isAxiosError(error) && error.response) {
    const data: unknown = error.response.data
    const code = isRecord(data)
      ? isNonEmptyString(data.code)
        ? data.code
        : isNonEmptyString(data.message)
        ? data.message
        : 'HTTP_ERROR'
      : 'HTTP_ERROR'
    throw new ReportFactoryError(code, error.response.status)
  }
  throw new ReportFactoryError('NETWORK_ERROR')
}

const sendRequest = async (
  apiOrigin: string,
  path: string,
  config: AxiosRequestConfig,
  options?: RequestOptions
) => {
  try {
    return await axiosInstance.request<unknown>({
      ...config,
      url: `${apiOrigin}${path}`,
      headers: { ...config.headers, Authorization: getAuthorization() },
      timeout: 30000,
      cancelToken: options?.cancelToken,
    })
  } catch (error) {
    return normalizeError(error)
  }
}

export const getReportFactoryConfig = async (
  options?: RequestOptions
): Promise<ReportFactoryConfig> => {
  const response = await sendRequest(
    getApiOrigin(),
    '/exports/mtd-hk/config',
    { method: 'GET' },
    options
  )
  if (response.status !== 200 || !isReportFactoryConfig(response.data)) {
    throw new ReportFactoryError('INVALID_RESPONSE', response.status)
  }
  return response.data
}

export const createReportFactoryJob = async (
  request: ReportFactoryRequest,
  idempotencyKey: string,
  options?: RequestOptions
): Promise<ReportFactoryAcceptedJob> => {
  if (
    !isReportFactoryRequest(request) ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      idempotencyKey
    )
  ) {
    throw new ReportFactoryError('INVALID_INPUT')
  }
  const apiOrigin = getApiOrigin()
  const response = await sendRequest(
    apiOrigin,
    '/exports/mtd-hk/jobs',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      data: request,
    },
    options
  )
  if (response.status !== 202 || !isAcceptedJob(response.data, apiOrigin)) {
    throw new ReportFactoryError('INVALID_RESPONSE', response.status)
  }
  return response.data
}

export const getReportFactoryJob = async (
  jobId: string,
  options?: RequestOptions
): Promise<ReportFactoryJob> => {
  if (!isNonEmptyString(jobId)) throw new ReportFactoryError('INVALID_INPUT')
  const response = await sendRequest(
    getApiOrigin(),
    getJobPath(jobId),
    { method: 'GET' },
    options
  )
  if (response.status !== 200 || !isJob(response.data, jobId)) {
    throw new ReportFactoryError('INVALID_RESPONSE', response.status)
  }
  return response.data
}
